import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { app, functions, auth } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
const db = getFirestore(app);
const DEFAULT_TIMEOUT_SECONDS = 300;
const OTP_TTL_SECONDS = 15 * 60;

const DEFAULT_DEVICE_VOLUME = 100;
const VOLUME_FIELDS = [
  { key: 'ringtoneVolume', label: 'Ringtone Volume' },
  { key: 'callVolume', label: 'Call Audio Volume' },
  { key: 'assistantVolume', label: 'Assistant Volume' },
];

function formatOtpCountdown(totalSeconds) {
  const parsedSeconds = Number.isFinite(totalSeconds) ? totalSeconds : 0;
  const safeSeconds = Math.max(0, parsedSeconds);
  const minutes = Math.floor(safeSeconds / 60).toString().padStart(2, '0');
  const seconds = (safeSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function mapGenerateOtpError(error) {
  switch (error && error.code) {
    case 'permission-denied':
      return 'You are not authorized to generate device OTPs. Ensure you are signed in as an admin with access to this participant.';
    case 'unauthenticated':
      return 'Authentication required. Please sign in again and retry OTP generation.';
    case 'not-found':
      return 'This participant could not be found. Refresh the page and verify the participant still exists.';
    case 'failed-precondition':
      return 'This participant is not assigned to a domain yet. Update the participant before generating an OTP.';
    case 'invalid-argument':
      return 'Participant information is incomplete. Reload the page and try again.';
    default:
      return `Failed to generate OTP: ${(error && error.message) || 'unknown error'}`;
  }
}

export default function ParticipantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [participant, setParticipant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otp, setOtp] = useState(null);
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [otpSecondsRemaining, setOtpSecondsRemaining] = useState(0);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState(null);
  const [unlinkingDeviceIds, setUnlinkingDeviceIds] = useState([]);
  const [updatingDeviceIds, setUpdatingDeviceIds] = useState([]);
  const [volumeDrafts, setVolumeDrafts] = useState({});
  const [userRole, setUserRole] = useState(null);
  const [updatingSilenceDetector, setUpdatingSilenceDetector] = useState(false);

  const resolveCallTimeoutSeconds = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return DEFAULT_TIMEOUT_SECONDS;
  };

  const isSilenceDetectorEnabled = (value) => value !== false;

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'admins', auth.currentUser?.uid || 'none'), (snap) => {
      if (snap.exists()) {
        setUserRole(snap.data().role);
      } else {
        setUserRole(null);
      }
    });
    return () => {
      if (typeof unsub === 'function') {
        unsub();
      }
    };
  }, []);

  useEffect(() => {
    const ref = doc(db, 'participants', id);
    getDoc(ref).then(snap => {
      if (!snap.exists()) {
        alert('Participant not found');
        navigate('/participants');
        return;
      }
      setParticipant({ id: snap.id, ...snap.data() });
    }).catch(e => console.error('fetch participant', e)).finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    if (!otpExpiresAt) {
      setOtpSecondsRemaining(0);
      return undefined;
    }

    let timerId = null;

    const updateCountdown = () => {
      const remainingSeconds = Math.max(0, Math.ceil((otpExpiresAt - Date.now()) / 1000));
      setOtpSecondsRemaining(remainingSeconds);

      if (remainingSeconds === 0 && timerId !== null) {
        window.clearInterval(timerId);
        timerId = null;
      }
    };

    updateCountdown();
    timerId = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(timerId);
  }, [otpExpiresAt]);

  useEffect(() => {
    // Wait for ID and Participant
    if (!id || !participant) {
      if (!id) {
        setDevices([]);
        setDevicesLoading(false);
      }
      return;
    }

    setDevicesLoading(true);
    setDevicesError(null);

    const queryConstraints = [where('bondedToParticipantId', '==', id)];

    // Only apply the domainId filter if the user is NOT a System Admin
    // System Admins can see "legacy" devices that might be missing the domainId field
    if (userRole !== 'SYSTEM_ADMIN' && participant.domainId) {
      queryConstraints.push(where('domainId', '==', participant.domainId));
    }

    const devicesQuery = query(
      collection(db, 'devices'),
      ...queryConstraints
    );

    const unsubscribe = onSnapshot(
      devicesQuery,
      (snapshot) => {
        const items = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setDevices(items);
        setVolumeDrafts((prev) => {
          if (!prev || Object.keys(prev).length === 0) {
            return prev;
          }
          const retained = {};
          for (const item of items) {
            const draft = prev[item.id];
            if (!draft) {
              continue;
            }
            const remaining = {};
            for (const [fieldKey, draftValue] of Object.entries(draft)) {
              const raw = item[fieldKey];
              let current = null;
              if (typeof raw === 'number') {
                current = raw;
              } else if (typeof raw === 'string') {
                const parsed = parseInt(raw, 10);
                if (!Number.isNaN(parsed)) {
                  current = parsed;
                }
              }
              if (current === draftValue) {
                continue;
              }
              remaining[fieldKey] = draftValue;
            }
            if (Object.keys(remaining).length > 0) {
              retained[item.id] = remaining;
            }
          }
          return retained;
        });
        setDevicesLoading(false);
      },
      (error) => {
        console.error('fetch participant devices', error);
        setDevicesError('Failed to load devices for this participant.');
        setDevicesLoading(false);
      }
    );

    return () => unsubscribe();
  }, [id, participant, userRole]);

  const formatLastSeen = useMemo(() => {
    return (lastSeenValue) => {
      if (!lastSeenValue) {
        return '—';
      }

      try {
        if (typeof lastSeenValue.toDate === 'function') {
          return lastSeenValue.toDate().toLocaleString();
        }
      } catch (err) {
        console.warn('Failed to convert lastSeen to date', err);
      }

      if (lastSeenValue instanceof Date) {
        return lastSeenValue.toLocaleString();
      }

      if (typeof lastSeenValue === 'number') {
        return new Date(lastSeenValue).toLocaleString();
      }

      if (typeof lastSeenValue === 'string') {
        return lastSeenValue;
      }

      return '—';
    };
  }, []);

  const handleUnlinkDevice = async (deviceId) => {
    if (!participant) {
      return;
    }

    const confirmed = window.confirm('Unlinking will disconnect this device from the participant. Continue?');
    if (!confirmed) {
      return;
    }

    setUnlinkingDeviceIds((prev) => (prev.includes(deviceId) ? prev : [...prev, deviceId]));

    try {
      const deviceRef = doc(db, 'devices', deviceId);
      await updateDoc(deviceRef, {
        bondedToParticipantId: null,
        isBonded: false,
        lastSeen: serverTimestamp(),
      });

      if (participant.currentDeviceId === deviceId) {
        const participantRef = doc(db, 'participants', participant.id);
        await updateDoc(participantRef, {
          currentDeviceId: '',
        });
        setParticipant((prev) => (prev ? { ...prev, currentDeviceId: '' } : prev));
      }

      alert('Device unlinked successfully.');
    } catch (error) {
      console.error('unlink device', error);
      alert(`Failed to unlink device: ${(error && error.message) || 'unknown error'}`);
    } finally {
      setUnlinkingDeviceIds((prev) => prev.filter((item) => item !== deviceId));
    }
  };

  const handleInterfaceModeChange = async (deviceId, newMode) => {
    try {
      await updateDoc(doc(db, 'devices', deviceId), { 
        interfaceMode: newMode,
        lastSeen: serverTimestamp() 
      });
    } catch (error) {
      console.error('update interface mode', error);
      alert('Failed to update interface mode.');
    }
  };

  const handleKeepScreenOnChange = async (deviceId, value) => {
    if (!deviceId) return;
    setUpdatingDeviceIds((prev) => (prev.includes(deviceId) ? prev : [...prev, deviceId]));
    try {
      await updateDoc(doc(db, 'devices', deviceId), {
        keepScreenOn: value
      });
    } catch (error) {
      console.error('update keepScreenOn', error);
      alert('Failed to update keep screen on setting.');
    } finally {
      setUpdatingDeviceIds((prev) => prev.filter((id) => id !== deviceId));
    }
  };

  const handleSilenceDetectorChange = async (value) => {
    if (!participant) {
      return;
    }

    setUpdatingSilenceDetector(true);
    try {
      await updateDoc(doc(db, 'participants', participant.id), {
        silenceDetectorEnabled: value,
        updatedAt: serverTimestamp(),
      });
      setParticipant((prev) => (prev ? { ...prev, silenceDetectorEnabled: value } : prev));
    } catch (error) {
      console.error('update silence detector', error);
      alert('Failed to update silence detector setting.');
    } finally {
      setUpdatingSilenceDetector(false);
    }
  };

  const resolveVolumeValue = (device, fieldKey) => {
    const draft = volumeDrafts[device.id];
    if (draft && typeof draft[fieldKey] === 'number') {
      return draft[fieldKey];
    }
    const raw = device[fieldKey];
    if (typeof raw === 'number') {
      return raw;
    }
    if (typeof raw === 'string') {
      const parsed = parseInt(raw, 10);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return DEFAULT_DEVICE_VOLUME;
  };

  const handleVolumeChange = (deviceId, fieldKey) => (_, newValue) => {
    const rawValue = Array.isArray(newValue) ? newValue[0] : newValue;
    if (typeof rawValue !== 'number' || Number.isNaN(rawValue)) {
      return;
    }
    const clamped = Math.min(100, Math.max(0, Math.round(rawValue)));
    setVolumeDrafts((prev) => ({
      ...prev,
      [deviceId]: {
        ...(prev[deviceId] || {}),
        [fieldKey]: clamped,
      },
    }));
  };

  const handleVolumeCommit = (device, fieldKey, label) => async (_, newValue) => {
    const rawValue = Array.isArray(newValue) ? newValue[0] : newValue;
    const fallback = resolveVolumeValue(device, fieldKey);
    const numeric = typeof rawValue === 'number' && !Number.isNaN(rawValue)
      ? rawValue
      : fallback;
    const clamped = Math.min(100, Math.max(0, Math.round(numeric)));

    try {
      await updateDoc(doc(db, 'devices', device.id), { [fieldKey]: clamped });
    } catch (error) {
      console.error('update device volume', error);
      alert(`Failed to update ${label.toLowerCase()}. Please try again.`);
    }
  };

  const handleActivate = async () => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      if (!participant) {
        throw new Error('Participant data is not available yet.');
      }
      
      if (!auth || !auth.currentUser) {
        throw new Error('No authenticated user — please sign in');
      }
      
      let tokenResult;
      try {
        tokenResult = await auth.currentUser.getIdTokenResult(true);
        console.debug('ID token refreshed; uid=%s admin=%s', auth.currentUser.uid, tokenResult?.claims?.admin);
      } catch (tokenErr) {
        console.warn('Failed to refresh ID token before calling function', tokenErr);
      }
      
      if (!tokenResult || !tokenResult.claims || !tokenResult.claims.admin) {
        setOtp(null);
        setOtpExpiresAt(null);
        setOtpError('Your account is not recognized as an admin. Please sign out and sign in again.');
        return;
      }
      
      const fn = httpsCallable(functions, 'generateDeviceOTP');
      const res = await fn({
        participantId: participant.id,
        deviceId: participant.currentDeviceId || '',
      });

      const generatedOtp = res.data?.otp ?? null;
      if (!generatedOtp) {
        throw new Error('OTP service did not return a code.');
      }
      
      console.debug('OTP generated successfully:', generatedOtp);
      setOtp(generatedOtp);
      setOtpExpiresAt(Date.now() + (OTP_TTL_SECONDS * 1000));
    } catch (e) {
      console.error('generate OTP error details:', {
        code: e && e.code,
        message: e && e.message,
        details: e && e.details,
        stack: e && e.stack
      });
      setOtp(null);
      setOtpExpiresAt(null);
      setOtpError(mapGenerateOtpError(e));
    } finally {
      setOtpLoading(false);
    }
  };

  if (loading) return <div>Loading participant...</div>;
  if (!participant) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5">Participant: {participant.name}</Typography>
      <Typography>Status: {participant.status}</Typography>
      <Typography>Call Timeout: {resolveCallTimeoutSeconds(participant.callTimeoutSeconds)} seconds</Typography>
      <Typography>Silence Detector: {isSilenceDetectorEnabled(participant.silenceDetectorEnabled) ? 'Enabled' : 'Disabled'}</Typography>
      <Typography>Linked Rooms: {(participant.linkedRoomIds || []).join(', ')}</Typography>
      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <Switch
            checked={isSilenceDetectorEnabled(participant.silenceDetectorEnabled)}
            onChange={(e) => handleSilenceDetectorChange(e.target.checked)}
            disabled={updatingSilenceDetector}
          />
        }
        label="Silence detector enabled"
      />
      <Box sx={{ mt: 2 }}>
        <Button component={Link} to={`/participants/${id}/edit`} variant="outlined">Edit</Button>
        <Button onClick={handleActivate} sx={{ ml: 1 }} variant="contained" disabled={otpLoading}>
          {otpLoading ? 'Generating...' : 'Activate New Device'}
        </Button>
        {otpError && (
          <Typography color="error" sx={{ mt: 1 }} role="alert">
            {otpError}
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">Linked Devices</Typography>
        {devicesLoading && <Typography sx={{ mt: 1 }}>Loading devices...</Typography>}
        {devicesError && (
          <Typography color="error" sx={{ mt: 1 }}>
            {devicesError}
          </Typography>
        )}
        {!devicesLoading && !devicesError && devices.length === 0 && (
          <Typography sx={{ mt: 1 }}>No devices are currently linked to this participant.</Typography>
        )}
        {!devicesLoading && !devicesError && devices.length > 0 && (
          devices.map((device) => {
            const unlinking = unlinkingDeviceIds.includes(device.id);
            return (
              <Paper key={device.id} sx={{ mt: 2, p: 2 }}>
                <Typography variant="subtitle1">Device ID: {device.id}</Typography>
                <Typography variant="body2">Bonded: {device.isBonded ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2">Last seen: {formatLastSeen(device.lastSeen)}</Typography>
                
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    Interface Mode
                  </Typography>
                  <ToggleButtonGroup
                    value={device.interfaceMode || 'VOICE'}
                    exclusive
                    onChange={(e, newMode) => {
                      if (newMode !== null) handleInterfaceModeChange(device.id, newMode);
                    }}
                    size="small"
                    color="primary"
                  >
                    <ToggleButton value="VOICE">VOICE (Orb)</ToggleButton>
                    <ToggleButton value="TOUCH">TOUCH (Roster)</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box sx={{ mt: 2, mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!device.keepScreenOn}
                        onChange={(e) => handleKeepScreenOnChange(device.id, e.target.checked)}
                        disabled={unlinking || updatingDeviceIds.includes(device.id)}
                        color="primary"
                      />
                    }
                    label="Keep Screen On (Prevent display sleep)"
                  />
                </Box>

                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {VOLUME_FIELDS.map((field) => {
                    const currentValue = resolveVolumeValue(device, field.key);
                    return (
                      <Box key={field.key}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {field.label}: {currentValue}%
                        </Typography>
                        <Slider
                          min={0}
                          max={100}
                          step={1}
                          value={currentValue}
                          valueLabelDisplay="auto"
                          onChange={handleVolumeChange(device.id, field.key)}
                          onChangeCommitted={handleVolumeCommit(device, field.key, field.label)}
                          disabled={unlinking}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleUnlinkDevice(device.id)}
                    disabled={unlinking}
                  >
                    {unlinking ? 'Unlinking…' : 'Unlink Device'}
                  </Button>
                </Box>
              </Paper>
            );
          })
        )}
      </Box>
      {otp && (
        <Paper 
          sx={{ 
            mt: 4, 
            p: 4, 
            bgcolor: '#0A0A0A', 
            textAlign: 'center',
            opacity: otpLoading ? 0.6 : 1,
            transition: 'opacity 0.2s ease-in-out'
          }}
          aria-live="polite"
          role="status"
        >
          <Typography variant="subtitle1" sx={{ color: '#ffffff', mb: 2 }}>Device OTP Code</Typography>
          <Typography sx={{ fontSize: '48pt', fontWeight: 'bold', color: '#00F2FF', letterSpacing: 8, fontFamily: 'monospace' }}>
            {otpLoading ? '------' : otp}
          </Typography>
          <Typography
            variant="h6"
            sx={{ mt: 2, color: '#ffffff', fontFamily: 'monospace' }}
            aria-live="polite"
            aria-atomic="true"
          >
            Expires in {formatOtpCountdown(otpSecondsRemaining)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: '#aaaaaa' }}>
            {otpLoading ? 'Generating new code...' : 'Enter this 6-digit code on the physical device to bind it.'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
